import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';
import { getColorValue, u } from '../../utilities';
import type { LensSpanProps } from '../../types';
const sizes = {
  medium: {
    totalSize: u(3), // 24px
    barHeight: u(2.25), // 18px
  },
};
const speeds = {
  fast: 1.2,
  slow: 1.7,
};

const AI_PRIMARY_ANIMATED_LINEAR_GRADIENT =
  'linear-gradient(270deg, #565ADD 10.58%, #DC43BE 41.83%, #565ADD 69.23%, #565ADD 96.63%)';
const GRADIENT_ANIMATION_SPEED = 2.0;
const BARS_COUNT = 5;
const BAR_WIDTH = u(0.25); // 2px = 0.25 units
const getBarHeight = props => sizes[props.size || 'medium'].barHeight;
const getTotalSize = props => sizes[props.size || 'medium'].totalSize;
const getAnimationSpeed = props => speeds[props.speed || 'fast'];
const ripple = keyframes`
  0%, 100% {
    transform: scaleY(0.3);
  }
  50% {
    transform: scaleY(1);
  }
`;

// We only animate the background when it's the ai-primary gradient
const backgroundAnimation = keyframes`
  0% {
    background-position: 0% center;
  }
  100% {
    background-position: 100% center;
  }
`;

// The fade-in is to mask that the bars start at the same height. Without this
// you can see the bars load in at full height then "glitch" into animation
const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

type WaveformLoaderWrapperProps = {
  size?: 'medium';
  color?: string;
};
type BarProps = {
  index: number;
  size?: 'medium';
  speed: 'fast' | 'slow';
  color?: string;
};

const WaveformLoaderWrapper = styled.span<WaveformLoaderWrapperProps>`
  display: inline-flex;
  align-items: center;
  justify-content: space-evenly;
  height: ${props => getTotalSize(props)};
  width: ${props => getTotalSize(props)};
  position: relative;
`;
const Bar = styled.span<BarProps>`
  width: ${BAR_WIDTH};
  height: ${props => getBarHeight(props)};
  background: ${props =>
    props.color === 'ai-primary'
      ? AI_PRIMARY_ANIMATED_LINEAR_GRADIENT
      : getColorValue(props.color)};
  background-size: ${props => getTotalSize(props)}
    ${props => getTotalSize(props)};
  background-position: ${props => {
    const barCenterRatio = (props.index + 1) / (BARS_COUNT + 1);
    const offsetRatio = barCenterRatio - 0.5;
    return `calc(${getTotalSize(props)} * ${offsetRatio}) center`;
  }};
  opacity: 0; /* Ensure it starts invisible */
  transform: scaleY(0.3);
  transform-origin: center;
  animation:
    ${fadeIn} 50ms ease-out forwards,
    ${ripple} ${props => getAnimationSpeed(props)}s ease-in-out infinite,
    ${backgroundAnimation} ${GRADIENT_ANIMATION_SPEED}s linear infinite;

  animation-delay: ${props =>
    -1 + props.index * (getAnimationSpeed(props) / BARS_COUNT)}s;
  position: relative;
`;

export const WaveformLoader = ({
  size = 'medium',
  speed = 'fast',
  color = 'body',
  ...props
}: WaveformLoaderProps &
  React.ComponentProps<typeof WaveformLoaderWrapper> &
  Omit<
    LensSpanProps,
    keyof WaveformLoaderProps &
      React.ComponentProps<typeof WaveformLoaderWrapper>
  >) => {
  const barsList = Array.from({ length: BARS_COUNT }, (_, index) => (
    <Bar key={index} index={index} size={size} speed={speed} color={color} />
  ));
  return (
    <WaveformLoaderWrapper size={size} color={color} {...props}>
      {barsList}
    </WaveformLoaderWrapper>
  );
};
export const availableSizes = Object.keys(sizes);
export const availableSpeeds = Object.keys(speeds);
type WaveformLoaderProps = {
  color?: string;
  size?: 'medium';
  speed?: 'fast' | 'slow';
};
export default WaveformLoader;
